import { FC, ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineX } from "react-icons/hi";
import { FilterCountBadge } from "./FilterCountBadge";

export interface SingleSelectFilterOption {
  /** The value compared against selectedKey (e.g. a driver email). */
  key: string;
  label: ReactNode;
}

export interface SingleSelectRadioFilterDropdownProps {
  containerRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  onToggle: (e: React.MouseEvent) => void;
  icon: ReactNode;
  label: string;
  options: SingleSelectFilterOption[];
  selectedKey?: string;
  onOptionClick: (key: string, e: React.MouseEvent) => void;
  headerTitle: string;
  /** Radio group name. */
  radioName: string;
  /** When provided, an in-header "Clear" button shows while a value is selected. */
  onClear?: () => void;
  /** Label span classes; defaults to "hidden md:inline ml-2". */
  labelClassName?: string;
  /** Trigger justify classes; defaults to "justify-center md:justify-between". */
  justifyClassName?: string;
  /** Full panel classes; defaults to the shared shell. */
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
}

const defaultPanelClassName =
  "absolute z-10 mt-1 bg-white rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600";

/**
 * Radio (single-select) filter dropdown shell. Reproduces the driver filter
 * dropdown repeated across the orders and locations pages.
 */
export const SingleSelectRadioFilterDropdown: FC<
  SingleSelectRadioFilterDropdownProps
> = ({
  containerRef,
  isOpen,
  onToggle,
  icon,
  label,
  options,
  selectedKey,
  onOptionClick,
  headerTitle,
  radioName,
  onClear,
  labelClassName = "hidden md:inline ml-2",
  justifyClassName = "justify-center md:justify-between",
  panelClassName = defaultPanelClassName,
  panelStyle,
}) => {
  const { t } = useTranslation(["common"]);
  const isActive = selectedKey !== undefined && selectedKey !== "";

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={onToggle}
        className={`flex items-center ${justifyClassName} bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 ${isActive ? "border-brand dark:border-brand" : ""}`}
      >
        <div className="flex items-center">
          {icon}
          <span className={labelClassName}>{label}</span>
          {isActive && <FilterCountBadge count={1} />}
        </div>
      </button>

      {isOpen && (
        <div
          className={panelClassName}
          style={panelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-border">
            <div className="flex justify-between items-center">
              <span className="font-medium dark:text-white">{headerTitle}</span>
              {onClear && isActive && (
                <button
                  className="text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                >
                  <HiOutlineX className="mr-1 h-4 w-4 inline" />
                  {t("common:Clear")}
                </button>
              )}
            </div>
          </div>
          <ul className="py-2 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <li key={option.key}>
                <button
                  onClick={(e) => onOptionClick(option.key, e)}
                  className={`flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 ${selectedKey === option.key ? "bg-brand-muted dark:bg-lgb-blue-400/30" : ""}`}
                >
                  <input
                    type="radio"
                    checked={selectedKey === option.key}
                    onChange={() => {}}
                    className="mr-2 w-4 h-4 text-brand bg-gray-100 border-gray-300 rounded-full focus:ring-brand dark:focus:ring-brand dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    name={radioName}
                  />
                  <span className="dark:text-white">{option.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
